import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { useTranslations } from 'next-intl';
import { Ui } from '@silvertours/front-shared';
import { Runtime } from '@silvertours/front-entities';
import { MenuTriggerButton } from './RegionalSettings.styles';

/**
 * Shared flag + selected-currency trigger content and its accessible label,
 * used both by the desktop flyout button and the mobile menu trigger.
 */
export const useRegionalSettingsTrigger = (): {
  ariaLabel: string;
  flag: ReactElement;
} => {
  const t = useTranslations(
    'features.navigationLegacy.navigation.header.regionalSettings',
  );
  const { market } = Runtime.useLocale();
  const { selectedCurrency, currencies } = Runtime.useSelectedCurrency();

  const hasCurrencies = !!currencies?.length;
  const ariaLabel = hasCurrencies
    ? `${t('trigger')}, ${selectedCurrency}`
    : t('trigger');
  const flag = (
    <Ui.Flag
      country={market}
      translation={{ label: hasCurrencies ? selectedCurrency : undefined }}
    />
  );

  return { ariaLabel, flag };
};

type Props = {
  onClick: () => void;
} & Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'aria-expanded'
>;

/**
 * Button showing the active market flag and selected currency. Used as the
 * regional settings entry point inside the mobile menu; clicking it opens the
 * regional settings submenu.
 */
const RegionalSettingsTrigger = ({ onClick, className, ...aria }: Props) => {
  const { ariaLabel, flag } = useRegionalSettingsTrigger();

  return (
    <MenuTriggerButton
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={className}
      {...aria}
    >
      {flag}
    </MenuTriggerButton>
  );
};

export { RegionalSettingsTrigger };
